import React, { useState } from "react";
import { motion } from "framer-motion";
import { Boxes, ShieldCheck, Download, CheckCircle2, Link2, AlertTriangle, Unplug, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { http, downloadJSON } from "../../lib/api";
import { Panel, Btn, Overline } from "../ui";
import { useI18n } from "../../lib/i18n";

const DROP_FROM = { opacity: 0, y: -6 };
const DROP_TO = { opacity: 1, y: 0 };
const SLIDE_FROM = { opacity: 0, x: -10 };
const SLIDE_TO = { opacity: 1, x: 0 };

export default function CustodyLedger({ blocks, drills, onRefresh, onTamper, onRestore }) {
  const { t } = useI18n();
  const [verify, setVerify] = useState(null);
  const [busy, setBusy] = useState(false);
  const activeDrill = (drills || []).find((d) => !d.restored);

  const runVerify = async () => {
    setBusy(true);
    try {
      const { data } = await http.post("/worm/verify");
      setVerify(data);
      if (data.verified) toast.success(t("cust.ok", { n: data.total }));
      else toast.error(t("cust.bad", { b: data.broken_blocks.join(", ") }));
      onRefresh?.();
    } catch {
      toast.error("Verification failed");
    } finally {
      setBusy(false);
    }
  };

  const exportLedger = async () => {
    try {
      const { data } = await http.get("/worm/export");
      downloadJSON(`veralix-aegis-custody-${Date.now()}.json`, data);
      toast.success("Ledger exported");
    } catch {
      toast.error("Export failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="screen-custody">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Overline>{t("cust.overline")}</Overline>
          <h1 className="mt-1.5 font-heading text-3xl font-light tracking-tighter text-slate-100">{t("cust.title")}</h1>
          <p className="mt-2 text-sm text-slate-400">{t("cust.sub")}</p>
        </div>
        <div className="flex gap-2">
          <Btn variant="emerald" onClick={runVerify} disabled={busy} testid="worm-verify-btn">
            <ShieldCheck size={15} /> {busy ? t("cust.verifying") : t("cust.verify")}
          </Btn>
          <Btn variant="ghost" onClick={exportLedger} testid="worm-export-btn">
            <Download size={15} /> {t("cust.export")}
          </Btn>
        </div>
      </div>

      {verify && (
        <motion.div
          initial={DROP_FROM}
          animate={DROP_TO}
          className={`flex flex-wrap items-center gap-3 rounded-lg border p-4 ${
            verify.verified ? "border-aegis-emerald/40 bg-aegis-emerald/10" : "border-aegis-red/40 bg-aegis-red/10"
          }`}
          data-testid="worm-verify-result"
        >
          {verify.verified ? (
            <CheckCircle2 className="text-aegis-emerald" size={20} />
          ) : (
            <AlertTriangle className="text-aegis-red" size={20} />
          )}
          <span className={`text-sm ${verify.verified ? "text-aegis-emerald" : "text-aegis-red"}`}>
            {verify.verified ? t("cust.ok", { n: verify.total }) : t("cust.bad", { b: verify.broken_blocks.join(", ") })}
          </span>
          <span className="ml-auto font-mono text-[11px] text-slate-400">{verify.checked_at}</span>
        </motion.div>
      )}

      <Panel
        icon={Unplug}
        title={t("drill.title")}
        subtitle={t("drill.sub")}
        accent={activeDrill ? "red" : "amber"}
        testid="tamper-drill-panel"
        actions={
          <div className="flex flex-wrap gap-2">
            <Btn variant="redGhost" onClick={onTamper} disabled={!!activeDrill} testid="tamper-btn">
              <Unplug size={15} /> {t("drill.start")}
            </Btn>
            <Btn variant="emerald" onClick={onRestore} disabled={!activeDrill} testid="restore-btn">
              <RotateCcw size={15} /> {t("drill.restore")}
            </Btn>
          </div>
        }
      >
        <div className="space-y-3">
          <p className="text-[13px] leading-relaxed text-slate-400">{t("drill.body")}</p>
          {activeDrill ? (
            <div
              className="rounded-lg border border-aegis-red/40 bg-aegis-red/10 px-4 py-3"
              data-testid="drill-active-banner"
            >
              <p className="font-mono text-[12px] text-aegis-red">
                {t("drill.active", { n: activeDrill.index })} · {activeDrill.tampered_at}
              </p>
              <p className="mt-1 break-all font-mono text-[10px] text-slate-400">
                forged: {activeDrill.forged_payload_digest}
              </p>
              <p className="mt-1 break-all font-mono text-[10px] text-slate-400">
                original: {activeDrill.original_payload_digest}
              </p>
            </div>
          ) : (
            <p className="rounded-lg border border-dashed border-edge px-4 py-3 text-[12px] text-slate-400">
              {t("drill.idle")}
            </p>
          )}
        </div>
      </Panel>

      <Panel icon={Boxes} title={t("cust.blocks")} subtitle={t("cust.newest", { n: blocks.length })} accent="emerald">
        <div className="relative pl-6">
          <span className="absolute left-[7px] top-2 bottom-2 w-px bg-gradient-to-b from-aegis-emerald/40 via-edge to-transparent" />
          <div className="space-y-4">
            {blocks.map((b, i) => {
              const broken =
                (verify && !verify.verified && verify.broken_blocks.includes(b.index)) || activeDrill?.index === b.index;
              return (
                <motion.div
                  key={b.index}
                  initial={SLIDE_FROM}
                  animate={SLIDE_TO}
                  transition={{ delay: Math.min(i * 0.03, 0.5) }}
                  className="relative"
                  data-testid={`worm-block-${b.index}`}
                >
                  <span className="absolute -left-[22px] top-4 grid place-items-center">
                    <span
                      className={`h-3 w-3 rounded-full border-2 bg-void ${broken ? "border-aegis-red" : "border-aegis-emerald"}`}
                    />
                  </span>
                  <div
                    className={`rounded-lg border raise p-4 transition-colors ${
                      broken ? "border-aegis-red/50" : "border-edge hover:border-aegis-emerald/30"
                    }`}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className={`font-mono text-sm ${broken ? "text-aegis-red" : "text-aegis-emerald"}`}>
                          BLOCK #{b.index}
                        </span>
                        <span className="font-mono text-xs text-slate-300">{b.action_type}</span>
                      </div>
                      <span className="font-mono text-[11px] text-slate-400">{b.timestamp}</span>
                    </div>
                    <div className="mt-3 grid gap-2 font-mono text-[11px]">
                      <div className="flex gap-2">
                        <span className="w-14 shrink-0 text-slate-400">Hash</span>
                        <span className="break-all text-aegis-emerald/90">{b.hash}</span>
                      </div>
                      <div className="flex gap-2">
                        <span className="w-14 shrink-0 text-slate-400">Prev</span>
                        <span className="break-all text-slate-400">{b.prev_hash}</span>
                      </div>
                    </div>
                    <div className="mt-3 flex items-center gap-1.5 text-[11px] text-slate-400">
                      <Link2 size={12} className="text-aegis-cyan" />
                      {t("cust.signer")}: <span className="text-slate-200">{b.steward_signer}</span>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </Panel>
    </div>
  );
}
